import * as React from 'react';
import { List, Avatar, Tag, Empty, Spin } from 'antd';
import { MailOutlined, PushpinOutlined, PaperClipOutlined } from '@ant-design/icons';
import moment from 'moment';
import './MailList.scss';

interface Email {
    _id: string;
    from: string;
    to: string[];
    subject: string;
    body?: string;
    mailContent?: {
        subject: string;
        body: string;
        attachments?: string[];
    };
    mailStatus?: string;
    createdAt: string;
    isPinned?: boolean;
    tags?: Array<{ type: string; name: string }>;
    attachments?: string[];
}

interface MailListProps {
    emails: Email[];
    selectedEmail: Email | null;
    onEmailSelect: (email: Email) => void;
    loading: boolean;
    folder: string;
}

const MailList: React.FC<MailListProps> = ({ emails, selectedEmail, onEmailSelect, loading, folder }) => {
    const groupEmailsByDate = (emails: Email[]) => {
        const now = moment();
        const today = moment().startOf('day');
        const lastWeek = moment().subtract(7, 'days');
        const lastMonth = moment().subtract(30, 'days');

        const groups: {
            pinned: Email[];
            today: Email[];
            lastWeek: Email[];
            lastMonth: Email[];
            older: Email[];
        } = {
            pinned: [],
            today: [],
            lastWeek: [],
            lastMonth: [],
            older: [],
        };

        emails.forEach((email) => {
            const emailDate = moment(email.createdAt);
            if (email.isPinned) {
                groups.pinned.push(email);
            } else if (emailDate.isSame(today, 'day')) {
                groups.today.push(email);
            } else if (emailDate.isAfter(lastWeek)) {
                groups.lastWeek.push(email);
            } else if (emailDate.isAfter(lastMonth)) {
                groups.lastMonth.push(email);
            } else {
                groups.older.push(email);
            }
        });

        return groups;
    };

    const renderEmailItem = (email: Email) => {
        const isSelected = selectedEmail?._id === email._id;
        const subject = email.mailContent?.subject || email.subject || 'No Subject';
        const preview = email.mailContent?.body || email.body || '';
        const hasAttachments = (email.attachments?.length || email.mailContent?.attachments?.length || 0) > 0;
        const attachmentCount = (email.attachments?.length || email.mailContent?.attachments?.length || 0);
        const emailDate = moment(email.createdAt);
        const isToday = emailDate.isSame(moment(), 'day');
        const timeDisplay = isToday ? emailDate.format('h:mm A') : emailDate.format('MMM DD');

        return (
            <List.Item
                className={`mail-list-item ${isSelected ? 'selected' : ''}`}
                onClick={() => onEmailSelect(email)}
            >
                <List.Item.Meta
                    avatar={
                        <Avatar
                            style={{
                                backgroundColor: '#1890ff',
                            }}
                            icon={<MailOutlined />}
                        />
                    }
                    title={
                        <div className="mail-item-header">
                            <div className="mail-item-sender">
                                {email.isPinned && (
                                    <PushpinOutlined className="pin-icon" style={{ color: '#1890ff', marginRight: 4 }} />
                                )}
                                <span className="sender-name">{email.from || 'Unknown'}</span>
                            </div>
                            <span className="mail-item-time">{timeDisplay}</span>
                        </div>
                    }
                    description={
                        <div className="mail-item-content">
                            <div className="mail-item-subject">{subject}</div>
                            <div className="mail-item-preview">{preview.substring(0, 100)}...</div>
                            <div className="mail-item-footer">
                                {email.tags && email.tags.length > 0 && (
                                    <div className="mail-item-tags">
                                        {email.tags.map((tag, index) => (
                                            <Tag key={index} color="blue" className="mail-tag">
                                                {tag.type}: {tag.name}
                                            </Tag>
                                        ))}
                                    </div>
                                )}
                                {hasAttachments && (
                                    <div className="mail-item-attachments">
                                        <PaperClipOutlined /> {attachmentCount} attachment{attachmentCount > 1 ? 's' : ''}
                                    </div>
                                )}
                            </div>
                        </div>
                    }
                />
            </List.Item>
        );
    };

    if (loading) {
        return (
            <div className="mail-list-loading">
                <Spin size="large" />
            </div>
        );
    }

    if (emails.length === 0) {
        return (
            <div className="mail-list-empty">
                <Empty description="No emails found" />
            </div>
        );
    }

    const groupedEmails = groupEmailsByDate(emails);

    return (
        <div className="mail-list">
            <div className="mail-list-header">
                <h3 className="mail-list-title">{folder.charAt(0).toUpperCase() + folder.slice(1)}</h3>
                <div className="mail-list-sort">
                    <span>Sort</span>
                </div>
            </div>
            <div className="mail-list-content">
                {groupedEmails.pinned.length > 0 && (
                    <div className="mail-group">
                        <div className="mail-group-title">PINNED</div>
                        <List
                            dataSource={groupedEmails.pinned}
                            renderItem={renderEmailItem}
                            className="mail-group-list"
                        />
                    </div>
                )}
                {groupedEmails.today.length > 0 && (
                    <div className="mail-group">
                        <div className="mail-group-title">TODAY</div>
                        <List
                            dataSource={groupedEmails.today}
                            renderItem={renderEmailItem}
                            className="mail-group-list"
                        />
                    </div>
                )}
                {groupedEmails.lastWeek.length > 0 && (
                    <div className="mail-group">
                        <div className="mail-group-title">LAST WEEK</div>
                        <List
                            dataSource={groupedEmails.lastWeek}
                            renderItem={renderEmailItem}
                            className="mail-group-list"
                        />
                    </div>
                )}
                {groupedEmails.lastMonth.length > 0 && (
                    <div className="mail-group">
                        <div className="mail-group-title">LAST MONTH</div>
                        <List
                            dataSource={groupedEmails.lastMonth}
                            renderItem={renderEmailItem}
                            className="mail-group-list"
                        />
                    </div>
                )}
                {groupedEmails.older.length > 0 && (
                    <div className="mail-group">
                        <div className="mail-group-title">&gt; LAST MONTH</div>
                        <List
                            dataSource={groupedEmails.older}
                            renderItem={renderEmailItem}
                            className="mail-group-list"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default MailList;
